import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { Input } from '@/components/ui/input';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { useBudgetType } from '@/hooks/use-budget-type';

interface BudgetProps {
  label?: string;
}

export function Budget({ label }: BudgetProps) {
  const intl = useIntl();
  const { getEditCampaignBudgetLabel } = useBudgetType();
  const { hookForm, isFetching, campaign, setActiveEditField, isCompleted } =
    useEditCampaignContext();
  const isReadonly = campaign?.is_readonly;

  const {
    register,
    formState: { errors },
    watch,
    setError,
    clearErrors,
  } = hookForm;

  const budget = watch('budget');
  const budgetType = watch('budget_type');

  useEffect(() => {
    if (
      !isCompleted &&
      (!budgetType || budgetType === 'lifetime') &&
      budget &&
      budget < (campaign?.budget_spent ?? 0)
    ) {
      setError('budget', {
        message: intl.formatMessage({
          defaultMessage: 'Total budget cannot be less than already spent',
        }),
      });
    } else {
      clearErrors('budget');
    }
  }, [
    budget,
    campaign?.budget_spent,
    budgetType,
    setError,
    clearErrors,
    intl,
    isCompleted,
  ]);

  return (
    <Input
      isNumber
      label={label ?? getEditCampaignBudgetLabel(budgetType)}
      {...register('budget', {
        setValueAs: value => (value === '' ? null : Number(value)),
      })}
      placeholderLabel="$"
      placeholder={intl.formatMessage({
        defaultMessage: 'Enter budget',
      })}
      error={errors.budget?.message}
      isFetching={isFetching}
      onFocus={() => setActiveEditField('budget')}
      onBlur={() => setActiveEditField(null)}
      readOnly={isCompleted || isReadonly}
    />
  );
}
